import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { useOpenModal } from '@flowus/common/next-modal';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Button } from 'src/common/components/button';
import { Icon } from 'src/common/components/icon';
import { request } from 'src/common/request';
import { useOpenTransfer } from 'src/hooks/public/use-open-transfer';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { Option } from '../option';
import { IncomeRecord } from './income-record';
import { Tooltip } from '@flowus/common/tooltip';
import { Modals } from '@flowus/shared';
import { publicLink } from 'src/common/const';

/**
 * 空间收益
 */
export const SpaceIncome: FC = () => {
  const { t } = useAppTranslation();
  const openTransfer = useOpenTransfer();
  const [cashInfo, setCashInfo] =
    useState<Awaited<ReturnType<typeof request.infra.getTotalInWallet>>>();
  const openModal = useOpenModal();

  useEffect(() => {
    const spaceId = getCurrentSpaceId();
    void request.infra.getTotalInWallet(spaceId).then(setCashInfo);
  }, []);

  const openIncomeRecord = () => {
    openModal.modal({
      modalId: Modals.INCOME_RECORD,
      content: () => <IncomeRecord />,
    });
  };

  if (!cashInfo) return null;
  // @ts-ignore typecheck
  const _withdrawingAmount = cashInfo?.withdrawingAmount;
  return (
    <div>
      <Option
        title={
          <div className="flex items-center space-x-2">
            <span>{t('components.space_info_setting.space_income.title')}</span>
            <div
              className="flex items-center space-x-0.5 text-grey3 animate-click"
              onClick={openIncomeRecord}
            >
              <span>{t('components.space_info_setting.space_income.view_records')}</span>
              <Icon name="IcArrowDown01" size="tiny" className="-rotate-90" />
            </div>
          </div>
        }
        description={
          <div>
            <div className="text-t2 px-4 py-2 bg-grey9 rounded space-y-2">
              <div className="text-t2-medium flex space-x-2">
                <span>{t('common.withdrawable')}</span>
                <span>{cashInfo.canWithdrawAmount}</span>
              </div>
              <Tooltip
                popup={t('components.space_info_setting.space_income.settlement_time')}
                className="inline-flex space-x-2 cursor-help"
              >
                <span className="underline underline-offset-4">
                  {t('components.space_info_setting.space_income.pending_settlement')}
                </span>
                <span>{cashInfo.inProgressAmount}</span>
              </Tooltip>
              {!!_withdrawingAmount && (
                <div className="text-t2-medium flex space-x-2">
                  <span>{t('components.space_info_setting.space_income.withdrawing')}</span>
                  <span>{_withdrawingAmount}</span>
                </div>
              )}
              <div className="flex space-x-2">
                <span>{t('components.space_info_setting.space_income.total_income')}</span>
                <span>{cashInfo.totalAmount}</span>
              </div>
              <div className="text-t5-medium">
                <span>{t('components.space_info_setting.space_income.withdrawal_rule')}</span>
                <a
                  className="ml-2 text-t5 underline"
                  href={publicLink.feeDescription}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('components.space_info_setting.space_income.fee_description')}
                </a>
              </div>
            </div>
          </div>
        }
        rightPart={
          <Button
            colorType="active"
            onClick={() => openTransfer({ type: 'space', spaceId: getCurrentSpaceId() })}
          >
            {t('components.space_info_setting.space_income.withdraw_now')}
          </Button>
        }
      />
    </div>
  );
};
